// React & React Hooks
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

// Third-party libraries
import _ from "lodash";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";

// MUI components
import { styled } from "@mui/material/styles";
import {
  Badge,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  Switch
} from "@mui/material";

// MUI icons
import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ZoomInMapSharpIcon from "@mui/icons-material/ZoomInMapSharp";
import ZoomOutMapSharpIcon from "@mui/icons-material/ZoomOutMapSharp";
import EmailIcon from "@mui/icons-material/Email";

// MUI x-tree-view
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view";

// Context / Providers
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Constants
import urlConstant from "../../constants/urlConstants";
import dataConstant from "../../constants/dataConstants";

// Services / utilities
import { httpPost } from "../../services/commonService";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";

// Redux
import { selectUserDetail } from "../../redux/slice/userSlice";
import CustomTextField from "../../shared-components/form-controls/customTextField";

const CustomTextFieldLeft = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "var(--inputs)",
    color: "var(--black)",
    fontSize: "0.9rem",
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    minHeight: "40px",
    "& fieldset": {
      border: "none",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "1rem",
    color: "var(--black)",
    marginBottom: theme.spacing(0.5),
  },
}));

let userData = [];
let treeDataList = [];
const SponsorTree = () => {
  const userDetail = useSelector(selectUserDetail);
  const authenticationState = useContext(AuthenticationContext);
  const [treeData, setTreeData] = useState([]);
  const [lastNodeAcctId, setLastNodeAcctId] = useState();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState([]);
  const [dialogData, setDialogData] = useState(null);
  const [userAcctId, setUserAcctId] = useState(0);
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isApicalled, setIsApicalled] = useState(false); 
  const [isToggled, setIsToggled] = useState(false);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [isSendFreeSignupLinkInvitationEmailDialogOpen, setIsSendFreeSignupLinkInvitationEmailDialogOpen] = useState(false);
  const [isBadgeFilterActive, setIsBadgeFilterActive] = useState(false);

  const { handleSubmit, setValue, ...formMethods } = useForm({
    mode: "all",
    defaultValues: {
      emailAddress: "",
    },
  });

  const formConfig = {
     nameField: {
      name: "receiverName",
      label: t("SponsorTree.Name Sending To"),
      maxLength: dataConstant.MinMaxLength.CharLimit100,
      required: true,
      type: "text",
    },
    emailField: {
      name: "receiverEmailAddress",
      label: t("SponsorTree.Email Address"),
      type: "email",
      maxLength: dataConstant.MinMaxLength.CharLimit100,
      required: true
    },
    signatureField: {
      name: "senderName",
      label: t("SponsorTree.From Signature"),
      maxLength: dataConstant.MinMaxLength.CharLimit100,
      required: true,
      type: "text",
    },
  };
  
  const handleFreeSignupLinkInvitationEmailDialogOpen = () => {
    formMethods.reset({
      receiverEmailAddress: "", 
      receiverName: "",
      senderName: "",
    });
    setIsSendFreeSignupLinkInvitationEmailDialogOpen(true);
  };

  const handleFreeSignupLinkInvitationEmailDialogClose = () => {
    setIsSendFreeSignupLinkInvitationEmailDialogOpen(false);
  };

  const handleAllowFreeBYLDerSignupToggle = async () => {
    const newToggleValue = !isToggled;
    const modelData = {
      LoginUserId: dialogData?.UserId,
      AcctID: dialogData?.acctID,
      AllowFreeBYLDerSignup: newToggleValue,
    };

    try {
      const response = await httpPost(
        urlConstant.User.UpdateAllowFreeBYLDerSignup,
        modelData,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );
      if (response?.Status === dataConstant.APIResponseStatus.Success) {
        setIsToggled(newToggleValue);
        // Update userData and dialogData so tree view and dialog stay in sync
        if (userData && userData[dialogData.acctID]) {
          userData[dialogData.acctID].AllowFreeBYLDerSignup = newToggleValue;
        }
        setDialogData(prev => ({
          ...prev,
          AllowFreeBYLDerSignup: newToggleValue
        }));
        showSnackbar(response.description, "success");
      } else {
        showSnackbar(response.error, "error");
      }
    } catch (error) {
      console.error("Toggle error:", error);
    }
  };

  const onSubmitSendInvitationFreeBYLDerSignupLink = async (data) => {
    const modelData = {
      AcctID: dialogData?.acctID,
      ReceiverEmailAddress: data.receiverEmailAddress,
      ReceiverName : data.receiverName,
      SenderName: data.senderName,
    };

    try {
      const response = await httpPost(
        urlConstant.User.SendInvitationFreeBYLDerSignupLink,
        modelData,
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      );

      if (response?.Status === dataConstant.APIResponseStatus.Success) {
        setIsSendFreeSignupLinkInvitationEmailDialogOpen(false);
        showSnackbar(response.description, "success");
      } else {
        showSnackbar(response.error, "error");
      }
    } catch (error) {
      console.error("Email send failed:", error);
    }
  };

  useEffect(() => {
    if (userDetail?.UserAccountDetails?.AcctID) {
      const getSponsorInfoURL = urlConstant.ReferralTree.GetReferralTreeList;
      const mainAccount = {
        acctID: userDetail.UserAccountDetails.AcctID,
        refID: userDetail.UserAccountDetails.DirectSponsorDetails.DSAcctID,
        RankID: userDetail.UserAccountDetails.RankID,
        RankName: userDetail.UserAccountDetails.RankName,
        AcctName: userDetail.UserAccountDetails.PvtAcctFullName,
        AcctStatus: userDetail.UserAccountDetails.AcctStatus,
        UserId: userDetail.UserAccountDetails.UserID,
        AchievedRankName: userDetail.UserAccountDetails.AchievedRankName,
        PackageType: userDetail.PurchasePackageDetails.LatestPackageType,
        HasBYLDerInfluencerPartnerAccess: userDetail.HasBYLDerInfluencerPartnerAccess,
        NextProcessDate: userDetail.PurchasePackageDetails.NextProcessDate,
        OrderEntryDate: userDetail.PurchasePackageDetails.OrderEntryDate,
        ExpirationDate: userDetail.PurchasePackageDetails.LatestExpirationDate,
      };
      httpPost(
        getSponsorInfoURL,
        {},
        true,
        true,
        authenticationState.accessToken,
        authenticationState.impersonateEmail
      )
        .then((response) => {
          const dataMap = {};
          const responseData = [mainAccount, ...response.Value];
          responseData.forEach((item) => {
            dataMap[item.acctID] = {
              ...item,
              children: [],
              isHighlight: false,
              PackageTypeStatus: item.PackageType?.toLowerCase().includes(
                "[inactive]"
              )
                ? "inactive"
                : "active",
            };
          });
          setUserAcctId(userDetail.UserAccountDetails.AcctID);
          const tree = [];
          responseData.forEach((item) => {
            if (dataMap[item.refID]) {
              dataMap[item.refID].children.push(dataMap[item.acctID]);
            } else {
              tree.push(dataMap[item.acctID]);
            }
          });
          userData = dataMap;
          setTreeData(tree);
          setExpanded([userDetail.UserAccountDetails.AcctID?.toString()]);
          treeDataList = tree;
          setLastNodeAcctId(tree[0]?.children?.at(-1)?.acctID);
          setIsApicalled(true);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [userDetail]);

  useEffect(() => {
    if (isApicalled) {
      handleSearch(searchText);
    }
  }, [searchText]);

  const iconColor = (packageType) => {
    if (packageType === "" || packageType === undefined) {
      return "#FFFFFF";
    } else if (packageType?.toLowerCase() === "affiliate") {
      return "#58B84A";
    } else if (packageType?.toLowerCase() === "consultant (core)") {
      return "#FFB752";
    } else if (packageType?.toLowerCase() === "consultant") {
      return "#6D9AE2";
    } else if (packageType?.toLowerCase() === "aventi customer") {
      return "#ee5d2c";
    } else if (packageType?.toLowerCase() === "aventi travel") {
      return "#283841";
    } else if (packageType?.toLowerCase() === "premier" || packageType?.toLowerCase() === "omni diamond") {
      return "#fff";
    } else if (packageType?.toLowerCase() === "elite" || packageType?.toLowerCase() === "omni platinum") {
      return "#e6e608";
    } else if (packageType?.toLowerCase().includes("[inactive]")) {
      return "#FF6C6C";
    }
  };

  const renderTree = (nodes = []) =>
    nodes.map((node, index) => {
      const isLastChild = index === nodes.length - 1;

      return (
        <TreeItem
          className={`cm-tree-inner${isLastChild ? " cm-last-child" : ""}`}
          key={node.acctID}
          itemId={node.acctID.toString()}
          label={
            <CustomNode
              node={node}
              name={node.AcctName}
              color={
                node.isHighlight
                  ? "var(--primary-darker)"
                  : "var(--tree-gradient)"
              }
            />
          }
        >
          {node.children && renderTree(node.children)}
        </TreeItem>
      );
  });

  const handleExpandAll = () => {
    const expandAllWorker = new Worker(
      new URL("../../workers/treeWorker.js", import.meta.url)
    );
    expandAllWorker.postMessage({
      userData: treeDataList,
      searchTerm: "",
      userAcctId,
      isExpandAll: true,
    });
    expandAllWorker.onmessage = function (event) {
      const { treeData, expandedIds } = event.data;
      setExpanded(expandedIds);
      expandAllWorker.terminate();
    };
  };

  const handleCollapseAll = () => {
    setExpanded([userAcctId.toString()]);
  };

  const handleSearch = (searchEmail, isBadgeClick = false) => {
    const term = searchEmail.trim();
     if (isBadgeClick) setIsBadgeFilterActive(true);     
    if (term  === "") {
      setTreeData(treeDataList);
      handleExpandAll();
      return;
    }
    const searchWorker = new Worker(
      new URL("../../workers/treeWorker.js", import.meta.url)
    );
    searchWorker.postMessage({
      userData,
      searchTerm: term,
      userAcctId,
      isExpandAll: false,
    });
    searchWorker.onmessage = function (event) {
      const { treeData, expandedIds } = event.data;
      setTreeData(treeData);
      setExpanded(expandedIds);
      searchWorker.terminate();
    };
  };

  const handleClickOpen = (node) => {
    setDialogData(node);
    setIsToggled(!!node.AllowFreeBYLDerSignup);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);   
  };

  const handleClearSearch = () => {
    setIsBadgeFilterActive(false);
    handleSearch("");
  };

  const CustomNode = ({ node, color, name }) => (
    <Stack
      className={
        node.acctID === lastNodeAcctId &&
        node.children.length !== 0 &&
        "last-sponsor-tree"
      }
    >
      <Box
        sx={(theme) => ({
          background: color,
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          borderRadius: 2,
          px: 2,
          py: 1,
          border: "1px solid var(--headerbg)",
          position: "relative",
          zIndex: "1",
          minWidth: { xs: "100%", sm: "300px" },
          width: { xs: "100%", sm: "auto" },
        })}
        onClick={(e) => {
          e.stopPropagation();
          handleClickOpen(node);
        }}
      >
        <Button
          variant="contained"
          sx={{
            px: { xs: 1, sm: 0.8 },
            py: { xs: 1, sm: 0.5 },
            background: iconColor(node.PackageType),
            minWidth: "auto",
          }}
          disableElevation
        >
          <img src="/icons/user-header.svg" alt="user" />
        </Button>
        <Typography
          variant="body2"
          color="secondary.text"
          className="tree-text"
          fontWeight="600"
        >
          {`${name} (${node.acctID.toString()})`}
        </Typography>
        <Chip
          label={node?.children?.length}
          size="medium"
          className="numbered"
          sx={{
            bgcolor: "var(--secondary-main)",
            color: "var(--black)",
            fontSize: "16px",
            borderRadius: "10px",
            marginLeft: "auto",
            fontWeight: 600,
            "& .MuiChip-label": {
              padding: "7px",
              minWidth: "35px",
              textAlign: "center",
            },
          }}
        />
      </Box>
    </Stack>
  );

  return (
    <>
      <Box width={"100%"}>
        <Typography
          color="secondary.light"
          display={{ xs: "block", md: "none" }}
          variant="h4"
          fontSize={"24px"}
          fontWeight={600}
          mb={"20px"}
        >
          {t("My Team")}
        </Typography>
        <Card
          sx={(theme) => ({
            p: {
              xs: "20px",
              sm: "25px 30px",
            },
            background: theme.palette.background.cardGradient,
            borderRadius: "20px",
            height: "100%",
          })}
        >
          <Typography
            color="secondary.light"
            mb={"20px"}
            variant="h4"
            fontWeight={600}
          >
            {t("SponsorTree.SponsorTree")}
          </Typography>
          <Card
            sx={(theme) => ({
              p: {
                xs: "20px",
                sm: "25px 30px",
              },
              background: theme.palette.common.black,
              borderRadius: "20px",
              height: "100%",
            })}
          >
            <Stack gap={"20px"}>
              <Stack
                direction="row"
                flexWrap={"wrap"}
                sx={{
                  gap: { xs: "10px", lg: "50px" },
                  justifyContent: { xs: "flex-start", sm: "center" },
                }}
              >
                <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => handleSearch("inactive",true)}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("[Inactive]"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Inactive accounts
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => handleSearch("consultant (core)", true)}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("consultant (core)"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    CORE
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => handleSearch("consultant", true)}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("consultant"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Consultants
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => handleSearch("affiliate", true)}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("affiliate"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Affiliates
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => handleSearch("aventi customer", true)}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("aventi customer"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Aventi Travel
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => handleSearch("aventi travel" ,true)}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("aventi travel"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    Aventi Travel Business
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => handleSearch("elite", true)}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("elite"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    ELITE
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ cursor: "pointer" }} onClick={() => handleSearch("premier", true)}>
                  <Badge
                    badgeContent=""
                    sx={{
                      backgroundColor: iconColor("premier"),
                      height: "20px",
                      width: "20px",
                      overflow: "hidden",
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography
                    color="secondary.text"
                    sx={{ fontSize: "12px" }}
                    variant="body2"
                  >
                    PREMIER
                  </Typography>
                </Stack>
                {isBadgeFilterActive && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleClearSearch}
                  >
                    {t('SponsorTree.ClearSearch')}
                  </Button>
                )}
              </Stack>
            </Stack>
            <Card
              sx={(theme) => ({
                p: "20px",
                background: theme.palette.common.headerbg,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                boxShadow: "none",
                my: "20px",
                width: "100%",
                flexDirection: { xs: "column", sm: "row" },
              })}
            >
              <Stack
                sx={{
                  flexDirection: { xs: "column", sm: "row" },
                  width: { xs: "100%", sm: "auto" },
                }}
                gap={2}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleExpandAll}
                  startIcon={<ZoomOutMapSharpIcon />}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  {t("SponsorTree.Expand all")}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleCollapseAll}
                  startIcon={<ZoomInMapSharpIcon />}
                >
                  {t("SponsorTree.Collapse all")}
                </Button>
              </Stack>
              <Stack
                className="customfield-label-left cm-normal-search"
                sx={{
                  maxWidth: "700px",
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "flex-end",
                }}
              >
                <Card className="customfield-label-inner">
                  <SearchOutlinedIcon />
                </Card>
                <CustomTextFieldLeft
                  fullWidth
                  variant="outlined"
                  onChange={(e) => {
                    const handler = setTimeout(() => {
                      setSearchText(e.target.value);
                    }, 1000);

                    return () => {
                      clearTimeout(handler);
                    };
                  }}
                />
              </Stack>
            </Card>
            <Box
              sx={{
                minHeight: 352,
                overflow: "auto",
                display: "inline-block",
                minWidth: { xs: "100%", sm: 250 },
                width: { xs: "100%", sm: "auto" },
                textAlign:
                  searchText && treeData.length === 0 ? "center" : "initial",
              }}
            >
              {searchText && treeData.length === 0 ? (
                <Typography color="secondary.text" sx={{ mt: 2 }}>
                  {t("SponsorTree.NoRecordsFound")}
                </Typography>
              ) : (
                <SimpleTreeView
                  expandedItems={expanded}
                  onExpandedItemsChange={(_, nodeIds) => setExpanded(nodeIds)}
                >
                  {renderTree(treeData)}
                </SimpleTreeView>
              )}
            </Box>
          </Card>
        </Card>
      </Box>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        sx={{ borderRadius: "20px", background: "transparent" }}
      >
        <Stack
          sx={(theme) => ({
            background: theme.palette.common.black,
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
          })}
        >
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {t("SponsorTree.UserDetail")}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
              p: 1,
              borderRadius: "8px",
            })}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent
          sx={(theme) => ({
            background: theme.palette.background.cardGradient,
            border: 0,
            borderBottomLeftRadius: "20px",
            borderBottomRightRadius: "20px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          })}
          dividers
        >
          <Button
            variant="contained"
            sx={{
              px: 0.8,
              py: { xs: 2, sm: 0.5 },
              width: "auto",
              backgroundColor: iconColor(dialogData?.PackageType),
            }}
            color="primary"
            disableElevation
          >
            <img src="/icons/user-header.svg" alt="user" />
          </Button>
          <Stack textAlign={"center"}>
            <Typography
              color="common.light"
              sx={{}}
              variant="h4"
              fontWeight={600}
            >
              {`${t("SponsorTree.Hi")} ${dialogData?.AcctName}`}
            </Typography>
            <Card
              className="bg-dark"
              sx={{
                py: 1,
                px: 2,
                textAlign: "center",
                width: {
                  xs: "100%",
                  xxl: "auto",
                },
                mt: "10px",
              }}
            >
              <Typography color="secondary.light" variant="body1">
                {dialogData?.RankName}
              </Typography>
            </Card>
          </Stack>
          <TableContainer
            sx={{ background: "transparent", boxShadow: "none" }}
            component={Paper}
          >
            <Table sx={{ background: "transparent" }} aria-label="simple table">
              <TableBody>
               {userDetail?.HasBYLDerInfluencerPartnerAccess 
                && dialogData?.acctID === userAcctId && (
                  <>                    
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.FreeBYLDerSignupLink")}
                          </Typography>
                        </Stack>
                      </TableCell>
                     <TableCell valign="middle" align="right">
                       <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                          <IconButton
                            onClick={handleFreeSignupLinkInvitationEmailDialogOpen}
                            size="small"
                            color="primary"
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </>
                )}
               {(userDetail?.HasBYLDerInfluencerPartnerAccess 
                && dialogData?.FreeBYLDerSignupToggleVisibility === true) 
                && dialogData?.acctID !== userAcctId && (
                  <>                    
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.AllowFreeBYLDerSignup")}
                          </Typography>
                        </Stack>
                      </TableCell>
                     <TableCell valign="middle" align="right">
                       <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                        <Switch
                          checked={isToggled}
                          onChange={handleAllowFreeBYLDerSignupToggle}
                          size="small"
                          color="primary"
                        />
                        {isToggled && (
                          <IconButton
                            onClick={handleFreeSignupLinkInvitationEmailDialogOpen}
                            size="small"
                            color="primary"
                          >
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow
                  sx={{
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                    "& td,& th": {
                      borderBottom: "1px solid var(--black) !important",
                      paddingLeft: "0px",
                      paddingRight: "0px",
                      paddingTop: "7px",
                      paddingBottom: "7px",
                    },
                  }}
                >                  
                  <TableCell>
                    <Stack direction={"row"} alignItems={"center"} gap={1}>
                      <Typography
                        color="secondary.text"
                        sx={{ fontSize: "12px" }}
                        variant="body2"
                      >
                        {t("SponsorTree.SponsoredBy")}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell valign="middle" align="right">
                    <Typography
                      color="common.white"
                      sx={{}}
                      variant="body1"
                      fontWeight={600}
                    >
                      {dialogData?.refID}
                    </Typography>
                  </TableCell>
                </TableRow>
                {/* Conditional rendering based on PackageType for inactive status */}
                {dialogData?.PackageType?.toLowerCase().includes(
                  "[inactive]"
                ) ? (
                  <>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.Status")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          sx={
                            dialogData?.AcctStatus?.toLowerCase().includes(
                              "inactive"
                            )
                              ? { color: "#FF6C6C" }
                              : { color: "common.white" }
                          }
                          variant="body1"
                          fontWeight={600}
                        >
                          {"Inactive"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.Date")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {dialogData?.ExpirationDate
                            ? new Date(
                                dialogData.ExpirationDate
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                              })
                            : "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </>
                ) : (
                  <>
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.Level")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {dialogData?.PackageType &&
                            (dialogData.OrderEntryDate
                              ? `${dialogData.PackageType} ${moment(dialogData.OrderEntryDate).format("MM/DD/YYYY")}`
                              : dialogData.PackageType)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {dialogData?.NextProcessDate && (
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.Date of Next Order")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {dialogData?.NextProcessDate
                          ? new Date(dialogData.NextProcessDate).toLocaleDateString("en-US")
                          : "-"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    )}
                    <TableRow
                      sx={{
                        "&:last-child td, &:last-child th": {
                          border: 0,
                        },
                        "& td,& th": {
                          borderBottom: "1px solid var(--black) !important",
                          paddingLeft: "0px",
                          paddingRight: "0px",
                          paddingTop: "7px",
                          paddingBottom: "7px",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Typography
                            color="secondary.text"
                            sx={{ fontSize: "12px" }}
                            variant="body2"
                          >
                            {t("SponsorTree.AchievementStatus")}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell valign="middle" align="right">
                        <Typography
                          color="common.white"
                          sx={{}}
                          variant="body1"
                          fontWeight={600}
                        >
                          {dialogData?.AchievedRankName}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>

       {/* Email Dialog for Free Bylder Signup */}
      <Dialog 
        open={isSendFreeSignupLinkInvitationEmailDialogOpen} 
        onClose={handleFreeSignupLinkInvitationEmailDialogClose}
      >
        <form onSubmit={handleSubmit(onSubmitSendInvitationFreeBYLDerSignupLink)}>
          <Stack
            className="popup-title-icon"
            sx={(theme) => ({
              background: theme.palette.common.black,
            })}
          >
            <DialogTitle>
              {t("SponsorTree.Send Free Bylder Invitation")}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleFreeSignupLinkInvitationEmailDialogClose}
              className="close-icon-bg"
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          
          <DialogContent
            sx={(theme) => ({
              background: theme.palette.background.cardGradient,
              border: 0,
              borderBottomLeftRadius: "20px",
              borderBottomRightRadius: "20px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              alignItems: "flex-start",
            })}
            dividers
          >
            <Stack width={"100%"} gap={2}>
                <CustomTextField
                methods={{ ...formMethods, setValue }}
                properties={formConfig.nameField}
              />
              <CustomTextField
                methods={{ ...formMethods, setValue }}
                properties={formConfig.emailField}
              />
               <CustomTextField
                methods={{ ...formMethods, setValue }}
                properties={formConfig.signatureField}
              />
            </Stack>
            <Stack
              width={"100%"}
              borderTop={"1px solid var(--headerbg)"}
              justifyContent={"space-between"}
              pt={"20px"}
              gap={2}
            >
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{ px: 3 }}
              >
                {t("SponsorTree.Send Invitation")}
              </Button>
            </Stack>
          </DialogContent>
        </form>
      </Dialog>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
};

export default SponsorTree;